/**
 * Hook to connect to the per-user asset library YDoc room.
 *
 * Connects to /ws/yjs/_library/default via WebSocket and returns
 * library nodes as ReactFlowNode[]. Only connects when enabled=true.
 */

import { useEffect, useRef, useState } from 'react'
import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'
import type { Node } from 'reactflow'
import { getConfiguredApiBase, getAuthToken } from '../api/client'
import { readNodeDataFromYMap } from './useYCanvas'

export interface UseLibraryYDocOptions {
  /** Only connect when true. Set to false to disconnect and save resources. */
  enabled?: boolean
}

export interface UseLibraryYDocResult {
  /** Library nodes as ReactFlow Node objects */
  nodes: Node[]
  /** Whether initial sync with server is complete */
  synced: boolean
  /** Whether WebSocket connection is live */
  connected: boolean
}

/**
 * Convert an HTTP(S) API base URL to a WebSocket URL.
 */
function toWsUrl(httpUrl: string): string {
  return httpUrl.replace(/^http/, 'ws')
}

export function useLibraryYDoc({
  enabled = true,
}: UseLibraryYDocOptions = {}): UseLibraryYDocResult {
  const [nodes, setNodes] = useState<Node[]>([])
  const [synced, setSynced] = useState(false)
  const [connected, setConnected] = useState(false)
  const ydocRef = useRef<Y.Doc | null>(null)
  const providerRef = useRef<WebsocketProvider | null>(null)

  useEffect(() => {
    if (!enabled) {
      // Clean up if disabled
      if (providerRef.current) {
        providerRef.current.destroy()
        providerRef.current = null
      }
      if (ydocRef.current) {
        ydocRef.current.destroy()
        ydocRef.current = null
      }
      setNodes([])
      setSynced(false)
      setConnected(false)
      return
    }

    const ydoc = new Y.Doc()
    ydocRef.current = ydoc
    let cancelled = false

    const connect = async () => {
      const token = await getAuthToken()
      if (cancelled) return

      const apiBase = getConfiguredApiBase()
      const wsBase = toWsUrl(apiBase)
      // Library endpoint: /ws/yjs/_library with room name "default"
      // The server constructs the full room name library:{user_id}/default from the JWT
      const wsUrl = `${wsBase}/ws/yjs/_library`
      const roomName = 'default'

      const params: Record<string, string> = {}
      if (token) {
        params['token'] = token
      }

      const provider = new WebsocketProvider(wsUrl, roomName, ydoc, {
        connect: true,
        params,
      })

      provider.on('status', ({ status }: { status: string }) => {
        setConnected(status === 'connected')
      })

      provider.on('sync', (isSynced: boolean) => {
        setSynced(isSynced)
      })

      providerRef.current = provider

      // Observe the 'nodes' map for changes and rebuild ReactFlow nodes
      const yNodes = ydoc.getMap('nodes') as Y.Map<Y.Map<unknown>>

      const rebuildNodes = () => {
        const result: Node[] = []
        yNodes.forEach((ynode: Y.Map<unknown>, nodeId: string) => {
          const nodeType = (ynode.get('type') as string) ?? ''
          const dataRaw = readNodeDataFromYMap(ynode)

          const node: Node = {
            id: nodeId,
            type: nodeType,
            position: {
              x: (ynode.get('x') as number) ?? 0,
              y: (ynode.get('y') as number) ?? 0,
            },
            data: {
              ...dataRaw,
              status: (ynode.get('status') as string) ?? 'idle',
              label: (ynode.get('label') as string) ?? nodeId,
            },
          }
          result.push(node)
        })
        setNodes(result)
      }

      yNodes.observeDeep(rebuildNodes)
      // Initial build once connected
      rebuildNodes()
    }

    connect()

    return () => {
      cancelled = true
      if (providerRef.current) {
        providerRef.current.destroy()
        providerRef.current = null
      }
      if (ydocRef.current) {
        ydocRef.current.destroy()
        ydocRef.current = null
      }
      setConnected(false)
      setSynced(false)
      setNodes([])
    }
  }, [enabled])

  return { nodes, synced, connected }
}
